import React, { useState, useEffect } from 'react';
import {
  Database,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock,
  Server,
  HardDrive,
  Upload
} from 'lucide-react';
import { serviceConnectionsService, ServiceConnection } from '../services/serviceConnectionsService';
import { connectionPersistenceService, ConnectionStatus } from '../services/connectionPersistenceService';

const ConnectionPersistenceStatus: React.FC = () => {
  const [connections, setConnections] = useState<ServiceConnection[]>([]);
  const [statuses, setStatuses] = useState<ConnectionStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    loadConnectionData();
    const interval = setInterval(loadConnectionData, 30000); // Actualizar cada 30 segundos
    return () => clearInterval(interval);
  }, []);

  const loadConnectionData = async () => {
    try {
      setIsLoading(true);
      const [connectionsData, statusesData] = await Promise.all([
        serviceConnectionsService.getAllServiceConnections(),
        Promise.resolve(connectionPersistenceService.getConnectionStatuses())
      ]);

      setConnections(connectionsData);
      setStatuses(statusesData);
    } catch (error) {
      console.error('Error cargando datos de conexiones:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMigrateAll = async () => {
    try {
      setIsMigrating(true);
      setMigrationResult(null);

      const result = await connectionPersistenceService.migrateAllConfigurations();
      setMigrationResult(result);

      // Reload data after migration
      await loadConnectionData();

      // Auto-hide result after 5 seconds
      setTimeout(() => setMigrationResult(null), 5000);
    } catch (error) {
      console.error('Error migrando configuraciones:', error);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleForceCheck = async (serviceName: string) => {
    try {
      await connectionPersistenceService.forceCheckService(serviceName);
      await loadConnectionData();
    } catch (error) {
      console.error(`Error verificando ${serviceName}:`, error);
    }
  };

  const getServiceDisplayName = (serviceName: string): string => {
    const names: Record<string, string> = {
      qonto: 'Qonto (Banking)',
      google: 'Google Calendar',
      openai: 'OpenAI (IA)',
      firebase: 'Firebase (Notificaciones)',
      sendgrid: 'SendGrid (Email)',
      fathom: 'Fathom Analytics',
      grafana: 'Grafana (Monitoreo)',
      wasabi: 'Wasabi (Almacenamiento)'
    };
    return names[serviceName] || serviceName;
  };

  const getStatusForService = (serviceName: string): ConnectionStatus | undefined => {
    return statuses.find(s => s.serviceName === serviceName);
  };

  const formatDate = (date?: Date): string => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Cargando estado de conexiones...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-2">Persistencia de Conexiones</h3>
            <p className="text-blue-100">
              Todas las configuraciones se guardan automáticamente en la base de datos
            </p>
          </div>
          <Database className="h-12 w-12 opacity-80" />
        </div>
      </div>

      {/* Migration Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Upload className="h-6 w-6 text-blue-500" />
            <div>
              <h4 className="font-semibold text-gray-900">Migración de Datos</h4>
              <p className="text-sm text-gray-600">
                Migra las configuraciones de localStorage a la base de datos
              </p>
            </div>
          </div>
          <button
            onClick={handleMigrateAll}
            disabled={isMigrating}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {isMigrating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Migrando...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Migrar Todo
              </>
            )}
          </button>
        </div>

        {migrationResult && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-800">
                Migración completada: {migrationResult.success} exitosas, {migrationResult.failed} fallidas
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Connections Status */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900 flex items-center">
            <Server className="h-5 w-5 mr-2 text-gray-600" />
            Estado de Servicios Conectados
          </h4>
        </div>

        <div className="divide-y divide-gray-200">
          {connections.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <HardDrive className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No hay servicios configurados</p>
              <p className="text-sm mt-1">Configura servicios en las pestañas correspondientes</p>
            </div>
          ) : (
            connections.map(connection => {
              const status = getStatusForService(connection.serviceName);

              return (
                <div key={connection.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Service Name */}
                      <div className="flex items-center mb-2">
                        <h5 className="text-lg font-medium text-gray-900">
                          {getServiceDisplayName(connection.serviceName)}
                        </h5>
                        {connection.isActive ? (
                          <span className="ml-3 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Activo
                          </span>
                        ) : (
                          <span className="ml-3 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Inactivo
                          </span>
                        )}
                      </div>

                      {/* Connection Details */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Guardado en BD:</span>
                          <div className="flex items-center mt-1">
                            <Database className="h-4 w-4 text-green-500 mr-1" />
                            <span className="text-gray-900">
                              {formatDate(connection.createdAt)}
                            </span>
                          </div>
                        </div>

                        <div>
                          <span className="text-gray-500">Última conexión:</span>
                          <div className="flex items-center mt-1">
                            <Clock className="h-4 w-4 text-blue-500 mr-1" />
                            <span className="text-gray-900">
                              {formatDate(connection.lastConnectedAt)}
                            </span>
                          </div>
                        </div>

                        <div>
                          <span className="text-gray-500">Última actualización:</span>
                          <div className="flex items-center mt-1">
                            <RefreshCw className="h-4 w-4 text-purple-500 mr-1" />
                            <span className="text-gray-900">
                              {formatDate(connection.updatedAt)}
                            </span>
                          </div>
                        </div>

                        {status && (
                          <div>
                            <span className="text-gray-500">Estado de conexión:</span>
                            <div className="flex items-center mt-1">
                              {status.isConnected ? (
                                <>
                                  <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                                  <span className="text-green-700">Conectado</span>
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                                  <span className="text-red-700">
                                    Desconectado ({status.consecutiveFailures} fallos)
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* API Key Info */}
                      {connection.apiKey && (
                        <div className="mt-3 text-xs text-gray-500 flex items-center">
                          <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                          Credenciales guardadas en base de datos
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <button
                      onClick={() => handleForceCheck(connection.serviceName)}
                      className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Verificar conexión"
                    >
                      <RefreshCw className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Database className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Sistema de Persistencia Automática</p>
            <p className="text-blue-700">
              Todas las configuraciones se guardan automáticamente en la base de datos Supabase.
              Esto previene la pérdida de configuración y permite la reconexión automática de servicios.
            </p>
            <ul className="list-disc list-inside mt-2 text-blue-700 space-y-1">
              <li>Las credenciales se almacenan de forma segura en la base de datos</li>
              <li>Verificación automática cada 5 minutos</li>
              <li>Reconexión automática hasta 3 intentos</li>
              <li>Historial completo de conexiones y errores</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionPersistenceStatus;
